import React from 'react';
import {
  PageHead, Section, Container, Eyebrow, PageTitle, Lede, SectionHead, Card, CardTitle, CardBody, Grid, Saying, LINKEDIN_URL, SUBSTACK_URL,
} from '../components/ui';
import { FOUNDED } from '../content/proof';

/** About. The firm first, the founder as its proof, then Ohio. Firm voice throughout; "I" only inside quotes. */

const CREDENTIALS = [
  'Three-time CIO: Nationwide, Magellan Health, Rackspace',
  'Founder of the NTT Innovation Institute, scaled from zero to $30M in revenue',
  'Two-time IBM Distinguished Engineer',
  'CIO Hall of Fame inductee',
  'Ohio CIO ORBIE Global Award',
  'Executive Advisor for AI Fluency, The Ohio State University Fisher College of Business',
  'NACD Certified Board Director',
  'Doctor of Business Administration candidate, researching engagement and productivity in hybrid human-AI teams',
];

const WhyWeExistPage = () => (
  <>
    <PageHead
      title="Who we are"
      description={`Right Brain Labs, founded ${FOUNDED} in New Albany, Ohio, by Srini Koushik. An AI capability firm that builds the human side of the human-AI partnership and leaves the practice running without us.`}
      path="/who-we-are"
    />

    <Section tone="page" reveal={false}>
      <Eyebrow>Who we are</Eyebrow>
      <PageTitle>We work on the human side of the human-AI partnership.</PageTitle>
      <Lede>Right Brain Labs was founded {FOUNDED} in New Albany, Ohio, on one conviction: as AI gets smarter, the humans running it have to get sharper, and that takes a practice, not a rollout. We build that practice inside organizations and leave it running without us.</Lede>
    </Section>

    <Section tone="card" id="how-we-think">
      <Grid cols={3}>
        <Card accent>
          <CardTitle>We drill.</CardTitle>
          <CardBody>Capability is built through reps on real work, with a coach, under pressure. Training ends with a certificate. Our work ends with a practice you run yourselves.</CardBody>
        </Card>
        <Card accent>
          <CardTitle>We never hold the pen on your work.</CardTitle>
          <CardBody>Your people make the decisions and produce the work. We make them better at it, then step back.</CardBody>
        </Card>
        <Card accent>
          <CardTitle>We are finite by design.</CardTitle>
          <CardBody>Every engagement has an end. The Apprenticeship ends. The capability stays.</CardBody>
        </Card>
      </Grid>
    </Section>

    <Section tone="page" id="founder" className="scroll-mt-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-4">
          <picture>
            <source type="image/webp" srcSet="/images/v2/hero-session-520.webp" />
            <img src="/images/v2/hero-session-520.jpg" alt="Srini Koushik" width="520" height="650" loading="lazy" className="w-full max-w-[420px] h-auto rounded-md border border-navy-200 dark:border-navy-700" />
          </picture>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 font-sans text-interface font-medium">
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="text-emerald-600 dark:text-emerald-300">LinkedIn</a>
            <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer" className="text-emerald-600 dark:text-emerald-300">Substack</a>
            <a href="https://www.srinikoushik.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 dark:text-emerald-300">srinikoushik.com</a>
          </div>
        </div>
        <div className="md:col-span-8">
          <Eyebrow>The founder</Eyebrow>
          <SectionHead>Srini Koushik. Practitioner first.</SectionHead>
          <div className="prose-rbl mt-5">
            <p>Srini Koushik has spent 35 years leading technology organizations from the inside, at IBM, Nationwide, Magellan Health, HP, NTT, and Rackspace, through enterprise computing, the internet, cloud, data, and now AI. He led as an operator practicing the Think, Do, Learn, Adapt loop, and he built the argument in this firm out of watching what each wave did to the people doing the work.</p>
            <p>He teaches that argument as Executive Advisor for AI Fluency at The Ohio State University Fisher College of Business, and he wrote it down in Thinking with AI. He is a Doctor of Business Administration candidate researching engagement and productivity in hybrid human-AI teams.</p>
          </div>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {CREDENTIALS.map((c) => (
              <li key={c} className="font-sans text-interface text-navy-600 dark:text-[#C9D2E0] pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[9px] before:h-2 before:w-2 before:rounded-full before:bg-emerald-500">{c}</li>
            ))}
          </ul>
        </div>
      </div>
    </Section>

    <Section tone="card" id="origin" className="scroll-mt-20">
      <Eyebrow>Why the firm exists</Eyebrow>
      <SectionHead>The world was learning to use AI. We wanted people to evolve with it.</SectionHead>
      <div className="prose-rbl mt-5">
        <p>After 35 years leading technology transformation at large enterprises, Srini Koushik saw a pattern repeat across three waves of disruption, automation, outsourcing, and now AI: the more accessible the technology became, the less deeply people engaged with it. As AI got smarter, human engagement got shallower. Leaders stopped asking the right questions. Teams optimized for speed over thinking. The long-term result was cognitive stagnation disguised as productivity.</p>
        <p>Most organizations treat AI as a software rollout: deploy the tools, train for compliance, measure adoption. That is why so many AI initiatives produce no measurable value. Every earlier technology took over the Do and left humans the Think. AI can appear to do both, and when an organization lets it run the whole loop, the result is the Cognitive Rust Belt.</p>
        <p>The conviction to fix that became the founding thesis of Right Brain Labs, on September 29, 2025. A pattern watched for three decades, and a decision to act on it before the damage became irreversible. Right Brain Labs exists to keep humans in the Mastery Loop. We are not here to slow AI down. We are here to make sure that when the dust settles, the people holding the tools are sharper than the tools.</p>
      </div>
      <Saying tag="Creative Exploration" className="mt-12">The machine knows what was. The human imagines what could be.</Saying>
    </Section>

    <section id="ohio" className="bg-navy-50 dark:bg-navy-900 py-section md:py-section-lg reveal scroll-mt-20">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <Eyebrow>Ohio</Eyebrow>
            <SectionHead>Built in Central Ohio, for the organizations the big platforms overlook.</SectionHead>
            <div className="prose-rbl mt-5">
              <p>Most Ohio businesses have fewer than 100 employees. They employ our neighbors, they reinvest where they are, and they feel the pressure from AI-enabled competitors every day. Every major AI platform, consulting firm, and training provider has decided they are too small to matter.</p>
              <p>They are exactly the right size. Small enough that when the leader understands something, the whole organization moves. Large enough that getting AI Fluency right creates a lasting advantage. Manufacturers in Central Ohio are a significant part of our work, and we intend to keep building here.</p>
            </div>
            <Saying className="mt-10">Columbus is not where I ended up. It is where I chose to come back to.</Saying>
          </div>
          <div className="md:col-span-5">
            <picture>
              <source type="image/webp" srcSet="/images/v2/room-800.webp 800w, /images/v2/room-1536.webp 1536w" sizes="(min-width: 700px) 40vw, 100vw" />
              <img src="/images/v2/room-800.jpg" srcSet="/images/v2/room-800.jpg 800w, /images/v2/room-1536.jpg 1536w" sizes="(min-width: 700px) 40vw, 100vw" alt="A working session with a Central Ohio leadership team" width="1536" height="1024" loading="lazy" className="w-full h-auto rounded-md border border-navy-200 dark:border-navy-700" />
            </picture>
          </div>
        </div>
      </Container>
    </section>
  </>
);

export default WhyWeExistPage;
